/* CJAS design tokens — lifted from cjas.no Elementor kit-1122.
   Do not invent brand values here. Dark-mode surfaces are the only
   additions, because cjas.no does not define a dark theme. */

@import url('https://fonts.googleapis.com/css2?family=El+Messiri:wght@400;500;600;700&family=PT+Sans:wght@400;700&family=Poppins:wght@400;500&family=Noto+Nastaliq+Urdu:wght@400;600&display=swap');

:root {
  /* Brand — from cjas.no */
  --green:        #086A3C;
  /* The logo vector CJAS supplied (brand/logo-original.svg) is drawn in
     #0B864D, a lighter green than the one their own site uses. It is not a
     token: 4.63:1 on white, 4.38:1 on cream (fails AA), 4.50:1 on tint —
     where #086A3C clears 6.3:1 everywhere. Every derived logo file paints
     with currentColor and inherits --green, so the mark cannot drift.
     Raise with CJAS alongside the two faults already found (CA-40, CA-84). */
  --gold:         #A0700F;
  --ink:          #000000;
  --white:        #FFFFFF;
  --tint-green:   #F1FFF9;
  --tint-cream:   #FFF8EA;
  --field-grey:   #817B70;

  /* Text-safe variants. The brand fills above stay exactly as cjas.no
     defines them; these are used ONLY where the colour carries text at
     normal size, because #A0700F on white is 4.36:1 and WCAG AA needs 4.50.
     Verified: gold-text 5.31 on white, 5.02 on cream, 4.89 on the page
     ground --bg, 4.59 on the gold-soft tag; grey-text 4.50 on white.
     CHANGED 2026-09-10 (CA-55, audit A11Y-X-01): was #986A0E, which passed
     on white (4.77) and cream (4.51) but failed on --bg #F4F6F4 (4.39) — the
     app's page ground — and on the admin's gold-soft tag (4.12). */
  --gold-text:    #8F630C;
  --grey-text:    #7C766C;
  /* Text sitting ON a gold fill. White on #A0700F is 4.36:1 and fails AA;
     ink on the same fill is 4.82:1 and passes. The fix is to flip the TEXT,
     not to darken the brand colour — the gold block stays exactly the gold
     cjas.no publishes. Never white on gold at normal size. */
  --gold-fill-text: #000000;
  /* Gold that sits ON the brand green. The two brand colours cannot touch:
     --gold is 1.54:1 on --green and --gold-text is 1.40:1, so BOTH vanish.
     This lightened gold is 4.83:1 and is the same value the TV banner
     palette uses for its accent (CA-40). Any gold on a green surface —
     the countdown, a banner, a filled card — uses this and nothing else. */
  --gold-on-green: #EFD9A6;
  /* 2026-09-12 (CA-81): named because the panel and the TV page had both
     hard-coded them. Soft gold is the TV's secondary line on green; deep
     green is the header gradient's far end. */
  --gold-on-green-soft: #FFD98A;
  --green-deep:   #04331D;
  --green-text:   #086A3C;
  --danger:       #c0392b;

  /* Derived (shading only, not new brand colours) */
  --green-dark:   #05512d;
  --green-soft:   rgba(8,106,60,.08);
  --gold-soft:    rgba(160,112,15,.12);
  --hairline:     rgba(0,0,0,.10);

  /* Surfaces — light */
  --bg:           #F4F6F4;
  --surface:      var(--white);
  --text:         var(--ink);
  --text-muted:   #5B6660;

  /* Type */
  --font-head:    'El Messiri', Georgia, serif;
  --font-body:    'PT Sans', system-ui, sans-serif;
  --font-form:    'Poppins', system-ui, sans-serif;
  --font-urdu:    'Noto Nastaliq Urdu', serif;

  /* Shape — from the kit */
  --r-btn:  4px;
  --r-img:  8px;
  --r-card: 12px;
  --pad-btn: 16px 25px;
}

[data-theme="dark"] {
  --bg:         #0C1310;
  --surface:    #141D18;
  --text:       #EAF1EC;
  --text-muted: #93A29A;
  --tint-green: #16241C;
  --tint-cream: #241F14;
  --hairline:   rgba(255,255,255,.12);
  --green-soft: rgba(8,106,60,.22);

  /* Text-safe variants again, because the light ones fail on a dark surface.
     Measured against --surface #141D18:
     gold-text #C9A227 7.12, green-text #4CC38A 7.78,
     grey-text #A8B0AA 7.76, danger #FF6B5E 6.17.
     The brand fills (--green, --gold) are NOT redefined here. */
  --gold-text:  #C9A227;
  --green-text: #4CC38A;
  --grey-text:  #A8B0AA;
  --danger:     #FF6B5E;
}

* { box-sizing: border-box; }
body { margin: 0; font-family: var(--font-body); font-size: 18px; color: var(--text); background: var(--bg); }
h1,h2,h3,h4,h5,h6 { font-family: var(--font-head); font-weight: 700; margin: 0; }
label, input, select, textarea { font-family: var(--font-form); font-size: 18px; }
input, select, textarea {
  color: var(--grey-text); border: 1px solid var(--field-grey);
  border-radius: var(--r-btn); padding: 12px 14px; background: var(--surface);
}
a { color: var(--green); text-decoration: none; }
a:hover { color: var(--gold-text); }
img { max-width: 100%; border-radius: var(--r-img); }

.btn {
  font-family: var(--font-head); font-weight: 600; font-size: 18px;
  background: var(--green); color: var(--white); border: 0;
  border-radius: var(--r-btn); padding: var(--pad-btn); cursor: pointer;
  transition: background .18s ease, transform .18s ease;
}
/* The hover state turns a green button gold. The label has to change with it
   — white on gold is 4.36:1. See --gold-fill-text. */
.btn:hover { background: var(--gold); color: var(--gold-fill-text); transform: translateY(-4.5px); }
.btn--gold { background: var(--gold); color: var(--gold-fill-text); }
.btn--gold:hover { background: var(--green); color: var(--white); }

/* Urdu / RTL */
[dir="rtl"] { direction: rtl; }
[lang="ur"], [lang="ur"] h1, [lang="ur"] h2, [lang="ur"] h3 { font-family: var(--font-urdu); line-height: 2.1; }
